import warning from 'warning';
import primitiveDuckCreator from './primitiveDuckCreator.js';
import createAction from '../createAction.js';
import DuckSelector from '../DuckSelector.js';
import createDeepEqualSelector from '../createDeepEqualSelector.js';
import hasStore from './checks/hasPayloadTypeCheck.js';

const legacyReducer = (state, action, duck) => {
    switch (action.type) {
        case duck.types.ADD: {
            warning(
                false,
                `
                The duck providing state at '${duck.path}' is trying to use the 'ADD' type and/or 'add' action creator which are deprecated and will be removed in later versions.
                Please use the concat or push action action creators. https://pages.ghe.coxautoinc.com/Autotrader/modular-redux/docs/primitive-array-duck-creator#creators
                `
            );
            const newState = [].concat(state, action.payload);
            return newState;
        }
        case duck.types.CONCAT:
        case duck.types.PUSH: {
            const newState = [].concat(state, action.payload);
            return newState;
        }

        case duck.types.CLEAR: {
            return [];
        }

        case duck.types.INSERT: {
            const newState = [].concat(state);
            const { index, value = [] } = action.payload;

            newState.splice(index, 0, ...[].concat(value));

            return newState;
        }

        case duck.types.RESET: {
            return duck.initialState;
        }

        case duck.types.REMOVE: {
            const newState = [].concat(state);

            newState.splice(action.payload, 1);

            return newState;
        }

        case duck.types.REVERSE: {
            const newState = [].concat(state);
            newState.reverse();
            return newState;
        }

        case duck.types.SET:
        case duck.types.SET_ARRAY: {
            hasStore(duck, action, 'array');
            return action.payload;
        }

        case duck.types.SPLICE: {
            const newState = [].concat(state);
            newState.splice(...action.payload);
            return newState;
        }

        case duck.types.UNSHIFT: {
            const newState = [].concat(action.payload, state);
            return newState;
        }

        default:
            return state;
    }
};

const rtkReducer = (builder, duck) => {
    builder
        .addCase(duck.types.ADD, (state, action) => {
            warning(
                false,
                `
                The duck providing state at '${duck.path}' is trying to use the 'ADD' type and/or 'add' action creator which are deprecated and will be removed in later versions.
                Please use the concat or push action action creators. https://pages.ghe.coxautoinc.com/Autotrader/modular-redux/docs/primitive-array-duck-creator#creators
                `
            );
            return [].concat(state, action.payload);
        })

        .addCase(duck.types.PUSH, (state, action) =>
            [].concat(state, action.payload)
        )

        .addCase(duck.types.CONCAT, (state, action) =>
            [].concat(state, action.payload)
        )

        .addCase(duck.types.INSERT, (state, action) => {
            const { index, value = [] } = action.payload;
            state.splice(index, 0, ...[].concat(value));
        })

        .addCase(duck.types.REMOVE, (state, action) => {
            const newState = [].concat(state);

            newState.splice(action.payload, 1);

            return newState;
        })

        .addCase(duck.types.REVERSE, (state, action) => {
            state.reverse();
        })

        .addCase(duck.types.SET_ARRAY, (state, action) => {
            hasStore(duck, action, 'array');
            return action.payload;
        })

        .addCase(duck.types.SPLICE, (state, action) => {
            state.splice(...action.payload);
        })

        .addCase(duck.types.UNSHIFT, (state, action) =>
            [].concat(action.payload, state)
        );
};

const arrayDuckCreator = (options) =>
    primitiveDuckCreator({
        primitiveState: [],
        ...options,
    }).extend({
        creators: (duck) => ({
            add: (...args) => ({
                type: duck.types.ADD,
                payload: [...args],
            }),
            concat: createAction(duck.types.CONCAT),
            insert: createAction(duck.types.INSERT),
            push: createAction(duck.types.PUSH),
            setArray: createAction(duck.types.SET_ARRAY),
            splice: (...args) => ({
                type: duck.types.SPLICE,
                payload: [...args],
            }),
            unshift: createAction(duck.types.UNSHIFT),
        }),
        reducer: options.useRTKReducer ? rtkReducer : legacyReducer,
        selectors: (duck) => ({
            selectLength: new DuckSelector((_selectors) =>
                createDeepEqualSelector(
                    _selectors.getDuckState,
                    (localState) => localState.length
                )
            ),
            selectIsEmpty: new DuckSelector((_selectors) =>
                createDeepEqualSelector(
                    _selectors.selectLength,
                    (length) => length === 0
                )
            ),
        }),
        types: [
            'ADD',
            'CONCAT',
            'INSERT',
            'PUSH',
            'REMOVE',
            'REVERSE',
            'SET_ARRAY',
            'SPLICE',
            'UNSHIFT',
        ],
    });

export { arrayDuckCreator as default };
